/** 값이 없을 때 표에 찍는 자리표시자. */
export const EMPTY_MARK = '—'

/**
 * 서버 시각은 UTC ISO-8601 **마이크로초**(`2026-08-01T05:12:44.120000Z`)다.
 * ES 표준 파서는 소수점 3자리까지만 규정하므로 초과 자릿수를 잘라 안전하게 파싱한다.
 *
 * ⚠️ 이 함수는 **표시 전용**이다. 상태·잔여일수 판정에 쓰지 않는다(C1).
 */
function toDate(iso: string | null | undefined): Date | null {
  if (!iso) return null
  const normalized = iso.replace(/\.(\d{3})\d+/, '.$1')
  const date = new Date(normalized)
  return Number.isNaN(date.getTime()) ? null : date
}

/**
 * 로케일 출력 문자열을 후처리하지 않고 **부품(parts)으로 직접 조립**한다.
 * 브라우저·Node 버전마다 구분자 공백이 달라 `replace` 체인은 깨지기 쉽다.
 */
const KST_PARTS_FMT = new Intl.DateTimeFormat('en-US', {
  timeZone: 'Asia/Seoul',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hour12: false,
})

function kstParts(date: Date): Record<string, string> {
  return Object.fromEntries(
    KST_PARTS_FMT.formatToParts(date)
      .filter((part) => part.type !== 'literal')
      .map((part) => [part.type, part.value])
  )
}

/** `2025.08.05` (KST). 값이 없으면 `—`. */
export function formatDate(iso: string | null | undefined): string {
  const date = toDate(iso)
  if (!date) return EMPTY_MARK
  const { year, month, day } = kstParts(date)
  return `${year}.${month}.${day}`
}

/** `2025.08.05 10:20` (KST). 값이 없으면 `—`. */
export function formatDateTime(iso: string | null | undefined): string {
  const date = toDate(iso)
  if (!date) return EMPTY_MARK
  const { year, month, day, hour, minute } = kstParts(date)
  // `hour12: false` 는 자정을 "24" 로 내는 구현이 있어 정규화한다.
  const normalizedHour = hour === '24' ? '00' : hour
  return `${year}.${month}.${day} ${normalizedHour}:${minute}`
}

/** `2025.08.05 ~ 2026.08.04`. 양쪽 다 없으면 `—`. */
export function formatDateRange(from: string | null | undefined, to: string | null | undefined): string {
  if (!from && !to) return EMPTY_MARK
  return `${formatDate(from)} ~ ${formatDate(to)}`
}

/** `288,000원`. */
export function formatAmount(amount: number | null | undefined): string {
  if (typeof amount !== 'number' || !Number.isFinite(amount)) return EMPTY_MARK
  return `${amount.toLocaleString('ko-KR')}원`
}

/**
 * 잔여 기간. 미보유(null)면 `—`.
 * 🚨 서버가 준 `remainingDays` 를 그대로 쓴다 — 클라이언트가 날짜 차를 다시 계산하지 않는다(C1).
 */
export function formatRemainingDays(remainingDays: number | null | undefined): string {
  if (typeof remainingDays !== 'number') return EMPTY_MARK
  return `${remainingDays.toLocaleString('ko-KR')}일 남음`
}
